import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Icon,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { ENTITY_TYPES, downloadEntityTemplate } from './dataImportUtils';

// Step 1 of the import flow: pick which entity type to import, see what it
// maps to, download a template with the exact expected columns, and (without
// needing to open that template) glance at which columns are required.
const EntityTypeSelector = ({ entityType, onEntityChange, entityConfig }) => {
  const [showColumns, setShowColumns] = useState(false);

  return (
    <>
      <MDTypography variant="h6" mb={2}>Step 1 &mdash; Choose Data Type</MDTypography>
      <FormControl fullWidth size="small">
        <InputLabel id="entity-type-label">Data Type</InputLabel>
        <Select
          labelId="entity-type-label"
          label="Data Type"
          value={entityType}
          onChange={onEntityChange}
        >
          {ENTITY_TYPES.map((e) => (
            <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {entityConfig && (
        <MDTypography variant="caption" color="text" display="block" mt={1.5}>
          {entityConfig.description}
        </MDTypography>
      )}

      <MDButton
        variant="outlined"
        color="info"
        size="small"
        fullWidth
        sx={{ mt: 2.5 }}
        startIcon={<Icon>download</Icon>}
        onClick={() => downloadEntityTemplate(entityConfig)}
      >
        Download Template
      </MDButton>

      {entityConfig && (
        <MDBox mt={1.5}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowColumns((v) => !v)}
          >
            <MDTypography variant="button" color="info" fontWeight="medium">
              {showColumns ? 'Hide' : 'View'} column format
            </MDTypography>
            <Icon color="info" fontSize="small">
              {showColumns ? 'expand_less' : 'expand_more'}
            </Icon>
          </MDBox>

          <Collapse in={showColumns}>
            <MDBox mt={1.5} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
              <Table size="small">
                <TableBody>
                  {entityConfig.columns.map((col) => (
                    <TableRow key={col.key}>
                      <TableCell sx={{ borderBottom: 'none', py: 0.75 }}>
                        <MDTypography variant="caption" fontWeight={col.required ? 'bold' : 'regular'}>
                          {col.label}
                          {col.required && (
                            <MDTypography component="span" variant="caption" color="error" ml={0.25}>
                              *
                            </MDTypography>
                          )}
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', py: 0.75 }} align="right">
                        <MDTypography variant="caption" color="text" fontStyle="italic">
                          {col.example || '—'}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" mt={0.75}>
              <MDTypography component="span" variant="caption" color="error">*</MDTypography>
              {' '}Required column
            </MDTypography>
          </Collapse>
        </MDBox>
      )}
    </>
  );
};

EntityTypeSelector.propTypes = {
  entityType: PropTypes.string.isRequired,
  onEntityChange: PropTypes.func.isRequired,
  entityConfig: PropTypes.shape({
    description: PropTypes.string,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        example: PropTypes.string,
      }),
    ),
  }),
};

EntityTypeSelector.defaultProps = {
  entityConfig: null,
};

export default EntityTypeSelector;
